import { useMemo, useState } from 'react'
import { FileText, Loader2, ArrowRight } from 'lucide-react'
import { useCompanies } from '@/services/admin/companies.service'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { REPORTS } from './reports-meta'

/** YYYY-MM-DD para <input type="date">. */
function toDateInput(date: Date): string {
  return date.toISOString().slice(0, 10)
}

export function ReportsManagement() {
  const { data: companies, isLoading } = useCompanies()

  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([])
  const defaultFrom = useMemo(
    () => toDateInput(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
    []
  )
  const defaultTo = useMemo(() => toDateInput(new Date()), [])
  const [from, setFrom] = useState(defaultFrom)
  const [to, setTo] = useState(defaultTo)
  const [allTime, setAllTime] = useState(false)

  function toggleCompany(id: string) {
    setSelectedCompanyIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    )
  }

  const invalidRange = !allTime && !!from && !!to && from > to

  function openReport(type: string) {
    const params = new URLSearchParams()
    if (selectedCompanyIds.length) params.set('companies', selectedCompanyIds.join(','))
    if (allTime) {
      params.set('allTime', '1')
    } else {
      if (from) params.set('from', from)
      if (to) params.set('to', to)
    }
    // Abre em nova aba: a tela de impressão fica separada da tela de seleção.
    window.open(`/super-admin/relatorios/${type}?${params}`, '_blank')
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          Relatórios
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Escolha as empresas e o período, depois gere o relatório desejado. Cada um abre numa tela
          pronta para impressão em PDF e exportação em CSV.
        </p>
      </div>

      {/* Filtros comuns */}
      <Card className="glass-card border-border/50">
        <CardHeader>
          <CardTitle className="text-base">Filtros</CardTitle>
          <CardDescription>Aplicados a todos os relatórios abaixo.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Empresas */}
          <div className="space-y-3">
            <Label>Empresas</Label>
            {isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Carregando empresas...
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {companies?.map((company) => (
                    <label
                      key={company.id}
                      className="flex items-center gap-2 rounded-md border border-border/50 px-3 py-2 cursor-pointer hover:bg-muted/40"
                    >
                      <Checkbox
                        checked={selectedCompanyIds.includes(company.id)}
                        onCheckedChange={() => toggleCompany(company.id)}
                      />
                      <span className="text-sm">{company.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Nenhuma selecionada = todas as empresas.
                </p>
              </>
            )}
          </div>

          {/* Período */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer w-fit">
              <Checkbox checked={allTime} onCheckedChange={(v) => setAllTime(v === true)} />
              <span className="text-sm">Todo o período (sem recorte de data)</span>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="report-from">Data início</Label>
                <Input
                  id="report-from"
                  type="date"
                  value={from}
                  max={to || undefined}
                  disabled={allTime}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="report-to">Data fim</Label>
                <Input
                  id="report-to"
                  type="date"
                  value={to}
                  min={from || undefined}
                  disabled={allTime}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
            </div>
            {invalidRange && (
              <p className="text-sm text-destructive">
                A data de início não pode ser depois da data de fim.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Lista dos 5 relatórios */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {REPORTS.map((r) => (
          <Card key={r.type} className="glass-card border-border/50 flex flex-col">
            <CardHeader>
              <CardTitle className="text-base">{r.title}</CardTitle>
              <CardDescription>{r.description}</CardDescription>
            </CardHeader>
            <CardContent className="mt-auto">
              <Button
                onClick={() => openReport(r.type)}
                disabled={invalidRange}
                className="gap-2"
                variant="outline"
              >
                Gerar relatório
                <ArrowRight className="w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
